/**
 * The documents the reader recognizes: what each is called, where it is filed, and the numbers
 * worth copying from it (box by box, as printed). Field keys are permanent: tax software exports
 * map them, so a key is never renamed or reused; a new box gets a new key.
 */
import { parseDollars } from "./money";

export type TaxDocField = { key: string; label: string; kind: "money" | "text" };
export type TaxDocType = {
  code: string;
  label: string;
  folder: string;
  /** A tax-year form: filed under the year its return is filed (tax year + 1). */
  yearly: boolean;
  fields: readonly TaxDocField[];
  /** How a checklist item or request names it ("W-2", "1099-INT", "mortgage interest"). */
  names: readonly RegExp[];
};

const m = (key: string, label: string): TaxDocField => ({ key, label, kind: "money" });
const t = (key: string, label: string): TaxDocField => ({ key, label, kind: "text" });

export const TAX_DOC_TYPES: readonly TaxDocType[] = [
  {
    code: "W-2",
    label: "Form W-2 (wages)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bw-?2(?!g)s?\b/i],
    fields: [
      t("employer_ein", "Employer EIN"),
      m("box1", "Box 1 — Wages, tips, other compensation"),
      m("box2", "Box 2 — Federal income tax withheld"),
      m("box3", "Box 3 — Social Security wages"),
      m("box4", "Box 4 — Social Security tax withheld"),
      m("box5", "Box 5 — Medicare wages and tips"),
      m("box6", "Box 6 — Medicare tax withheld"),
      t("box12", "Box 12 — Codes and amounts (e.g. D 5,000.00; DD 9,876.00)"),
      t("box13_retirement", "Box 13 — Retirement plan box checked (yes/no)"),
      t("box15_state", "Box 15 — State"),
      m("box16", "Box 16 — State wages"),
      m("box17", "Box 17 — State income tax"),
      m("box19", "Box 19 — Local income tax"),
    ],
  },
  {
    code: "1099-INT",
    label: "Form 1099-INT (interest)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?int\b/i, /\binterest income\b/i],
    fields: [m("box1", "Box 1 — Interest income"), m("box3", "Box 3 — Interest on U.S. savings bonds and Treasury obligations"), m("box4", "Box 4 — Federal income tax withheld"), m("box8", "Box 8 — Tax-exempt interest")],
  },
  {
    code: "1099-DIV",
    label: "Form 1099-DIV (dividends)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?div\b/i, /\bdividends?\b/i],
    fields: [
      m("box1a", "Box 1a — Total ordinary dividends"),
      m("box1b", "Box 1b — Qualified dividends"),
      m("box2a", "Box 2a — Total capital gain distributions"),
      m("box4", "Box 4 — Federal income tax withheld"),
      m("box5", "Box 5 — Section 199A dividends"),
      m("box7", "Box 7 — Foreign tax paid"),
      m("box12", "Box 12 — Exempt-interest dividends"),
    ],
  },
  {
    code: "1099-B",
    label: "Form 1099-B (sales of stock and other investments)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?b\b/i, /\bbrokerage\b/i, /\bsold (?:stock|investments)\b/i],
    fields: [m("proceeds", "Total proceeds"), m("cost_basis", "Total cost basis"), m("wash_sale", "Wash sale loss disallowed"), m("gain_loss", "Total gain or (loss)"), m("box4", "Box 4 — Federal income tax withheld")],
  },
  {
    code: "1099-COMPOSITE",
    label: "Brokerage consolidated 1099",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bconsolidated 1099\b/i, /\bcomposite\b/i, /\bbrokerage\b/i],
    fields: [
      m("int_box1", "1099-INT box 1 — Interest income"),
      m("div_box1a", "1099-DIV box 1a — Ordinary dividends"),
      m("div_box1b", "1099-DIV box 1b — Qualified dividends"),
      m("div_box2a", "1099-DIV box 2a — Capital gain distributions"),
      m("div_box7", "1099-DIV box 7 — Foreign tax paid"),
      m("b_proceeds", "1099-B — Total proceeds"),
      m("b_cost_basis", "1099-B — Total cost basis"),
      m("b_gain_loss", "1099-B — Total gain or (loss)"),
      m("withheld", "Federal income tax withheld (all forms)"),
    ],
  },
  {
    code: "1099-NEC",
    label: "Form 1099-NEC (contract work)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?nec\b/i, /\bnonemployee\b/i],
    fields: [m("box1", "Box 1 — Nonemployee compensation"), m("box4", "Box 4 — Federal income tax withheld")],
  },
  {
    code: "1099-MISC",
    label: "Form 1099-MISC",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?misc\b/i],
    fields: [m("box1", "Box 1 — Rents"), m("box2", "Box 2 — Royalties"), m("box3", "Box 3 — Other income"), m("box4", "Box 4 — Federal income tax withheld")],
  },
  {
    code: "1099-R",
    label: "Form 1099-R (retirement distributions)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?r\b/i, /\bretirement distribution/i, /\bpension\b/i, /\bira distribution/i],
    fields: [m("box1", "Box 1 — Gross distribution"), m("box2a", "Box 2a — Taxable amount"), m("box4", "Box 4 — Federal income tax withheld"), t("box7", "Box 7 — Distribution code(s)"), t("ira_sep_simple", "IRA/SEP/SIMPLE box checked (yes/no)"), m("box14", "Box 14 — State tax withheld")],
  },
  {
    code: "1099-G",
    label: "Form 1099-G (unemployment or state refund)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?g\b/i, /\bunemployment\b/i],
    fields: [m("box1", "Box 1 — Unemployment compensation"), m("box2", "Box 2 — State or local income tax refund"), m("box4", "Box 4 — Federal income tax withheld")],
  },
  {
    code: "1099-K",
    label: "Form 1099-K (card and app payments)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?k\b/i],
    fields: [m("box1a", "Box 1a — Gross amount of payment card/third party transactions"), m("box4", "Box 4 — Federal income tax withheld")],
  },
  {
    code: "1099-SA",
    label: "Form 1099-SA (HSA distributions)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1099-?sa\b/i],
    fields: [m("box1", "Box 1 — Gross distribution"), t("box3", "Box 3 — Distribution code")],
  },
  {
    code: "SSA-1099",
    label: "Form SSA-1099 (Social Security benefits)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bssa-?1099\b/i, /\bsocial security benefit/i],
    fields: [m("box5", "Box 5 — Net benefits"), m("box6", "Box 6 — Voluntary federal income tax withheld")],
  },
  {
    code: "1098",
    label: "Form 1098 (mortgage interest)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1098s?\b(?!-)/i, /\bmortgage interest\b/i],
    fields: [m("box1", "Box 1 — Mortgage interest received"), m("box2", "Box 2 — Outstanding mortgage principal"), m("box5", "Box 5 — Mortgage insurance premiums"), m("box6", "Box 6 — Points paid"), m("box10", "Box 10 — Other (often property tax paid)")],
  },
  {
    code: "1098-T",
    label: "Form 1098-T (tuition)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1098-?t\b/i, /\btuition\b/i],
    fields: [m("box1", "Box 1 — Payments received for qualified tuition"), m("box5", "Box 5 — Scholarships or grants"), t("box8", "Box 8 — At least half-time student (yes/no)")],
  },
  {
    code: "1098-E",
    label: "Form 1098-E (student loan interest)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1098-?e\b/i, /\bstudent loan interest\b/i],
    fields: [m("box1", "Box 1 — Student loan interest received")],
  },
  {
    code: "1095-A",
    label: "Form 1095-A (Marketplace health insurance)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b1095-?a\b/i, /\bhealth (?:insurance )?marketplace\b/i],
    fields: [m("annual_premium", "Line 33A — Annual enrollment premium"), m("annual_slcsp", "Line 33B — Annual second lowest cost silver plan premium"), m("annual_aptc", "Line 33C — Annual advance payment of premium tax credit")],
  },
  {
    code: "5498",
    label: "Form 5498 (IRA contributions)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b5498\b(?!-)/i, /\bira contribution/i],
    fields: [m("box1", "Box 1 — IRA contributions"), m("box10", "Box 10 — Roth IRA contributions"), m("box5", "Box 5 — Fair market value of account")],
  },
  {
    code: "5498-SA",
    label: "Form 5498-SA (HSA contributions)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\b5498-?sa\b/i, /\bhsa contribution/i],
    fields: [m("box2", "Box 2 — Total contributions made in the year")],
  },
  {
    code: "K-1",
    label: "Schedule K-1 (partnership, S corporation, trust or estate)",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bk-?1s?\b/i],
    fields: [
      t("entity_ein", "Entity EIN"),
      t("form", "From Form 1065, 1120-S or 1041"),
      m("ordinary_income", "Ordinary business income (loss)"),
      m("rental_income", "Net rental real estate income (loss)"),
      m("guaranteed_payments", "Guaranteed payments"),
      m("interest", "Interest income"),
      m("dividends", "Ordinary dividends"),
      m("capital_gain", "Net capital gain (loss)"),
      m("section_179", "Section 179 deduction"),
      m("distributions", "Distributions"),
      m("se_earnings", "Self-employment earnings (loss)"),
    ],
  },
  {
    code: "PROPERTY_TAX",
    label: "Property tax bill or receipt",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bproperty tax/i, /\breal estate tax/i],
    fields: [t("property", "Property address"), m("amount_paid", "Amount paid in the year")],
  },
  {
    code: "CHILD_CARE",
    label: "Child or dependent care statement",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bchild ?care\b/i, /\bdaycare\b/i, /\bdependent care\b/i],
    fields: [t("provider_tin", "Provider's EIN or SSN as printed (last 4 only if an SSN)"), t("provider_address", "Provider address"), m("amount_paid", "Amount paid in the year")],
  },
  {
    code: "CHARITY",
    label: "Charitable donation receipt",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bcharit/i, /\bdonation/i],
    fields: [m("amount", "Amount donated"), t("noncash", "Non-cash items (description)")],
  },
  {
    code: "ESTIMATED_PAYMENT",
    label: "Estimated tax payment confirmation",
    folder: "Tax Documents",
    yearly: true,
    names: [/\bestimated (?:tax )?payment/i, /\b1040-?es\b/i],
    fields: [t("agency", "Paid to (IRS or state)"), t("paid_on", "Date paid"), m("amount", "Amount paid")],
  },
  {
    code: "NOTICE",
    label: "IRS or state letter",
    folder: "Tax Notices",
    yearly: false,
    names: [/\bcp\d{2,4}\b/i, /\b(?:irs|state|comptroller|tax|revenue)\b[\w\s,&'-]{0,40}\b(?:notices?|letters?)\b/i],
    fields: [t("agency", "From (IRS or state)"), t("notice_number", "Notice number (e.g. CP2000)"), t("notice_date", "Date of the letter"), m("amount_due", "Amount due"), t("respond_by", "Respond by")],
  },
  {
    code: "PRIOR_RETURN",
    label: "Filed tax return",
    folder: "Tax Returns",
    yearly: true,
    names: [/\b(?:prior|last) year'?s? (?:tax )?return\b/i, /\bcopy of (?:your |the )?(?:\d{4} )?(?:tax )?returns?\b/i],
    fields: [t("form", "Form (1040, 1065, 1120-S...)"), m("agi", "Adjusted gross income (individuals)"), m("total_tax", "Total tax")],
  },
  {
    code: "BANK_STATEMENT",
    label: "Bank or card statement",
    folder: "Bank Statements",
    yearly: false,
    names: [/\bbank statements?\b/i, /\b(?:credit )?card statements?\b/i, /\b(?:checking|savings) (?:account )?statements?\b/i],
    fields: [t("account_last4", "Account (last 4 digits)"), t("period_end", "Statement end date"), m("ending_balance", "Ending balance")],
  },
  {
    code: "RECEIPT",
    label: "Receipt or invoice paid",
    folder: "Receipts",
    yearly: false,
    names: [/^(?!.*\b(?:charit\w*|donations?|day ?care|child ?care|dependent care|tuition|property tax)\b).*\breceipts?\b/i],
    fields: [t("vendor", "Paid to"), t("date", "Date"), m("amount", "Amount")],
  },
  {
    code: "ID",
    label: "Photo ID",
    folder: "Identity Documents",
    yearly: false,
    names: [/\b(?:driver'?s )?licen[cs]e\b/i, /\bphoto id\b/i, /\bpassport\b/i],
    fields: [t("id_type", "Type (driver's license, passport...)"), t("state", "Issuing state"), t("expires", "Expiration date")],
  },
  {
    code: "W-9",
    label: "Form W-9",
    folder: "W-9s",
    yearly: false,
    names: [/\bw-?9\b/i],
    fields: [t("business_name", "Name on the form")],
  },
  { code: "OTHER", label: "Something else", folder: "General", yearly: false, names: [], fields: [] },
];

export const TAX_DOC_CODES = TAX_DOC_TYPES.map((d) => d.code) as [string, ...string[]];

export function taxDocType(code: string | null | undefined): TaxDocType {
  return TAX_DOC_TYPES.find((d) => d.code === code) ?? TAX_DOC_TYPES[TAX_DOC_TYPES.length - 1]!;
}

/** Whose document it is, for a household return. */
export const DOC_OWNERS = ["taxpayer", "spouse", "joint", "dependent", "business", "unknown"] as const;
export type DocOwner = (typeof DOC_OWNERS)[number];

/** Why a document needs a person's attention, besides confirming what was read. */
export const READ_FLAGS = {
  blurry: "Too blurry to read",
  hard_to_read: "Partly hard to read: check the numbers",
  password: "Password-protected",
  duplicate: "Same file uploaded before",
  possible_duplicate: "Looks like a document already received",
  several_documents: "More than one document in this file",
  wrong_year: "A different tax year than the return",
} as const;
export type ReadFlag = keyof typeof READ_FLAGS;

/**
 * An amount as printed ("$1,234.56", "(1,234.56)", "1234", "-52.10") in cents, or null if it isn't
 * a plain amount. Parentheses mean a loss.
 */
export function amountCents(text: string | null | undefined): number | null {
  if (text === null || text === undefined) return null;
  let s = text.trim().replace(/^USD\s*/i, "");
  if (!s) return null;
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  try {
    const c = parseDollars(s);
    return negative ? -Math.abs(c) : c;
  } catch {
    return null;
  }
}

/** Where a document goes: its type's folder, and for tax-year forms the year the return is filed. */
export function placementFor(code: string, taxYear: number | null): { folder: string; year: number | null } {
  const type = taxDocType(code);
  return { folder: type.folder, year: type.yearly && taxYear ? taxYear + 1 : null };
}

const STOP = new Set(["inc", "llc", "corp", "corporation", "co", "company", "the", "of", "and", "na", "n.a", "bank", "group", "from", "form", "for", "lp", "llp", "pc", "pa", "ltd"]);
const words = (x: string) =>
  x
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 1 && !STOP.has(w));

/**
 * The request a document most likely answers: one that names its form ("Form W-2 from Acme Corp",
 * "W-2s from every employer"), preferring one that also names who issued it, then an open one
 * over one already answered. Null when no request names the form.
 */
export function matchRequest(read: { code: string; issuer: string | null; taxYear?: number | null }, requests: ReadonlyArray<{ id: string; title: string; status: string }>): string | null {
  const type = taxDocType(read.code);
  if (!type.names.length) return null;
  const issuer = new Set(words(read.issuer ?? ""));
  let best: { id: string; score: number } | null = null;
  for (const r of requests) {
    if (r.status !== "open" && r.status !== "submitted") continue;
    if (!type.names.some((re) => re.test(r.title))) continue;
    // A request that names a year asks for that year's form.
    const years = [...r.title.matchAll(/\b(19|20)\d{2}\b/g)].map((m) => Number(m[0]));
    if (read.taxYear && years.length && !years.includes(read.taxYear)) continue;
    const named = words(r.title).filter((w) => issuer.has(w)).length;
    const score = 10 + named * 5 + (r.status === "open" ? 1 : 0);
    if (!best || score > best.score) best = { id: r.id, score };
  }
  return best?.id ?? null;
}

/** Social Security numbers never stay in what was read: only their last four digits. */
export function scrubTaxpayerIds(text: string): string {
  return text.replace(/\b\d{3}[-\s.]?\d{2}[-\s.]?(\d{4})\b/g, "•••-••-$1");
}
